/* eslint-disable react-hooks/exhaustive-deps, no-empty */
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, User, LogOut, Store, MessageCircle, Heart, Gavel, Wallet as WalletIcon, Bell, Gift, Menu, X } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useWallet } from "../context/WalletContext";
import { useState, useEffect, useRef } from "react";
import { formatIDR } from "../lib/format";
import { LogoFull } from "./Logo";
import api from "../lib/api";

function ChatLink() {
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/chat/unread-count");
        if (alive) setUnread(data.unread || 0);
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [location.pathname]);
  return (
    <Link to="/chat" data-testid="nav-chat" className="relative p-2 hover:text-[#00F0FF]">
      <MessageCircle className="w-5 h-5" />
      {unread > 0 && (
        <span data-testid="chat-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#FF003C] text-white text-[10px] font-bold flex items-center justify-center rounded-full">
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  );
}


function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) { /* noop */ }
  };
  const refresh = load;

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (alive) setItems(data);
      } catch (e) { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const unread = items.filter((n) => !n.read).length;
  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };
  const markAllRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((arr) => arr.map((n) => ({ ...n, read: true })));
  };

  const openNotification = async (notification) => {
    if (!notification.read) await markRead(notification.id);
    setOpen(false);

    // Backend dapat menentukan tujuan spesifik. Fallback ini membuat
    // notifikasi order lama tetap dapat dibuka tanpa perlu migrasi data.
    const orderId = notification.meta?.order_id;
    let target = notification.meta?.target_url;
    if (!target && notification.kind === "seller_new_order" && orderId) {
      target = `/seller?tab=orders&order=${encodeURIComponent(orderId)}`;
    } else if (!target && orderId) {
      target = `/orders/${encodeURIComponent(orderId)}`;
    }

    // Hanya izinkan navigasi internal dari data notifikasi.
    if (typeof target === "string" && target.startsWith("/") && !target.startsWith("//")) {
      navigate(target);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen((v) => !v)} className="relative text-white/70 hover:text-white p-2">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span data-testid="notif-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#FF003C] text-white text-[10px] font-bold flex items-center justify-center rounded-full">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[480px] overflow-y-auto bg-[#050505] border border-white/15 shadow-2xl z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between p-3 border-b border-white/10 sticky top-0 bg-[#050505]">
            <p className="text-xs uppercase tracking-widest font-bold text-white">Notifikasi</p>
            {unread > 0 && <button onClick={markAllRead} className="text-[10px] text-[#00F0FF] hover:underline uppercase tracking-widest">Tandai semua dibaca</button>}
          </div>
          {items.length === 0 ? (
            <p className="text-white/50 text-xs text-center py-8 px-4">Belum ada notifikasi.</p>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                 onClick={() => openNotification(n)}
                className={`p-3 border-b border-white/5 cursor-pointer hover:bg-white/5 ${!n.read ? "bg-[#00F0FF]/5" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-2 h-2 bg-[#00F0FF] rounded-full mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold ${n.kind === "product_deleted" ? "text-[#FF6B6B]" : "text-white"}`}>{n.title}</p>
                    <p className="text-[11px] text-white/70 mt-1 whitespace-pre-wrap break-words">{n.message}</p>
                    <p className="text-[10px] text-white/40 mt-1">{new Date(n.created_at).toLocaleString("id-ID")}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const wallet = useWallet();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    navigate(`/browse?q=${encodeURIComponent(q)}`);
  };

  // Tutup menu mobile setiap kali pindah halaman
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isAdmin = user && user !== false && (user.email === "admin@ustore.com" || user.is_admin);
  const isSeller = user && user !== false && user.role === "seller";


  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-2xl bg-[#050505]/80 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <LogoFull height={44} />
        </Link>

        <form onSubmit={onSearch} className="flex-1 hidden md:flex max-w-xl" data-testid="header-search-form">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search games, items, top-up..."
              className="w-full bg-black border border-white/15 rounded-none focus:border-[#00F0FF] focus:outline-none text-white text-sm pl-10 pr-4 py-2.5"
            />
          </div>
        </form>

        <nav className="flex items-center gap-2 md:gap-4 ml-auto text-sm">
          <NavLink to="/browse" data-testid="nav-browse" className="hidden md:block text-white/70 hover:text-white px-3 py-2 uppercase font-bold tracking-widest text-xs">
            Browse
          </NavLink>
          <NavLink
            to="/auction"
            data-testid="nav-auction"
            className="hidden md:flex items-center gap-1 px-3 py-2 uppercase font-bold tracking-widest text-xs text-white hover:text-[#5C4DFF]"
          >
            <Gavel className="w-3.5 h-3.5" /> Auction
          </NavLink>
          <Link to="/cart" data-testid="nav-cart" className="relative p-2 hover:text-[#00F0FF]">
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-[#FFD700] text-black text-[10px] font-black px-1.5 py-0 leading-4 min-w-[16px] text-center"
              >
                {count}
              </span>
            )}
          </Link>

          {user && user !== false ? (
            <>
              <Link
                to="/wallet"
                data-testid="nav-wallet"
                className="hidden md:flex items-center gap-2 px-3 py-2 border border-[#00F0FF]/30 hover:border-[#00F0FF] bg-[#00F0FF]/5 text-[#00F0FF] text-xs uppercase font-bold tracking-widest"
              >
                <WalletIcon className="w-3.5 h-3.5" />
                <span data-testid="wallet-balance-header">{formatIDR(wallet.balance || 0, { compact: true })}</span>
              </Link>
              <Link to="/wishlist" data-testid="nav-wishlist" className="relative p-2 hover:text-[#FF003C]">
                <Heart className="w-5 h-5" />
                {wishCount > 0 && (
                  <span
                    data-testid="wishlist-count"
                    className="absolute -top-1 -right-1 bg-[#FF003C] text-white text-[10px] font-black px-1.5 py-0 leading-4 min-w-[16px] text-center"
                  >
                    {wishCount}
                  </span>
                )}
              </Link>
              <ChatLink />
              <NotificationBell />
              {user.role === "seller" ? (
                <Link to="/seller" data-testid="nav-seller" className="hidden md:flex items-center gap-1 px-3 py-2 border border-[#00F0FF]/40 text-[#00F0FF] text-xs uppercase font-bold tracking-widest hover:bg-[#00F0FF]/10">
                  <Store className="w-4 h-4" />
                  Seller
                </Link>
              ) : (
                <Link to="/become-seller" data-testid="nav-become-seller" className="hidden md:block px-3 py-2 text-xs uppercase font-bold tracking-widest text-white/70 hover:text-white">
                  Become Seller
                </Link>
              )}
              {(user.email === "admin@ustore.com" || user.is_admin) && (
                <Link to="/admin" data-testid="nav-admin" className="hidden md:flex items-center gap-1 px-3 py-2 border border-[#FF003C]/40 text-[#FF003C] text-xs uppercase font-bold tracking-widest hover:bg-[#FF003C]/10">
                  Admin
                </Link>
              )}
              <Link to="/referral" data-testid="nav-referral" className="hidden md:flex items-center gap-1 px-3 py-2 text-xs uppercase font-bold tracking-widest text-white/70 hover:text-[#00F0FF]" title="Program Referal">
                <Gift className="w-4 h-4" />
                Referral
              </Link>
              <Link to="/account" data-testid="nav-account" className="hidden md:flex items-center gap-2 px-3 py-2 text-xs uppercase font-bold tracking-widest text-white/80 hover:text-white">
                <User className="w-4 h-4" />
                {user.name?.split(" ")[0]}
              </Link>

              <button data-testid="nav-logout-btn" onClick={logout} className="p-2 text-white/60 hover:text-[#FF003C]">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn-ghost">
                Login
              </Link>
              <Link to="/register" data-testid="nav-register" className="btn-primary hidden md:inline-block">
                Register
              </Link>
            </>
          )}

          {/* Tombol hamburger - hanya mobile */}
          <button
            type="button"
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            className="md:hidden p-2 text-white/80 hover:text-white"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      {/* Panel menu mobile */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-2xl" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-3">
            {/* Search mobile */}
            <form onSubmit={onSearch} data-testid="mobile-search-form">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search games, items, top-up..."
                  className="w-full bg-black border border-white/15 rounded-none focus:border-[#00F0FF] focus:outline-none text-white text-sm pl-10 pr-4 py-2.5"
                />
              </div>
            </form>

            <nav className="grid grid-cols-1 gap-1 text-sm">
              <NavLink to="/browse" onClick={() => setMobileOpen(false)} data-testid="m-nav-browse" className="flex items-center gap-2 px-3 py-3 text-white/80 hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                <Search className="w-4 h-4" /> Browse
              </NavLink>
              <NavLink to="/auction" onClick={() => setMobileOpen(false)} data-testid="m-nav-auction" className="flex items-center gap-2 px-3 py-3 text-white hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                <Gavel className="w-4 h-4" /> Auction
              </NavLink>

              {user && user !== false ? (
                <>
                  <Link to="/wallet" onClick={() => setMobileOpen(false)} data-testid="m-nav-wallet" className="flex items-center justify-between px-3 py-3 text-[#00F0FF] hover:bg-white/5 uppercase font-bold tracking-widest text-xs border border-[#00F0FF]/30 bg-[#00F0FF]/5">
                    <span className="flex items-center gap-2"><WalletIcon className="w-4 h-4" /> Wallet</span>
                    <span data-testid="m-wallet-balance">{formatIDR(wallet.balance || 0, { compact: true })}</span>
                  </Link>
                  <Link to="/wishlist" onClick={() => setMobileOpen(false)} data-testid="m-nav-wishlist" className="flex items-center gap-2 px-3 py-3 text-white/80 hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                    <Heart className="w-4 h-4" /> Wishlist{wishCount > 0 ? ` (${wishCount})` : ""}
                  </Link>
                  <Link to="/chat" onClick={() => setMobileOpen(false)} data-testid="m-nav-chat" className="flex items-center gap-2 px-3 py-3 text-white/80 hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                    <MessageCircle className="w-4 h-4" /> Chat
                  </Link>

                  {isSeller ? (
                    <Link to="/seller" onClick={() => setMobileOpen(false)} data-testid="m-nav-seller" className="flex items-center gap-2 px-3 py-3 text-[#00F0FF] hover:bg-[#00F0FF]/10 uppercase font-bold tracking-widest text-xs border border-[#00F0FF]/40">
                      <Store className="w-4 h-4" /> Seller Dashboard
                    </Link>
                  ) : (
                    <Link to="/become-seller" onClick={() => setMobileOpen(false)} data-testid="m-nav-become-seller" className="flex items-center gap-2 px-3 py-3 text-white/80 hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                      <Store className="w-4 h-4" /> Become Seller
                    </Link>
                  )}

                  {isAdmin && (
                    <Link to="/admin" onClick={() => setMobileOpen(false)} data-testid="m-nav-admin" className="flex items-center gap-2 px-3 py-3 text-[#FF003C] hover:bg-[#FF003C]/10 uppercase font-bold tracking-widest text-xs border border-[#FF003C]/40">
                      Admin Dashboard
                    </Link>
                  )}

                  <Link to="/referral" onClick={() => setMobileOpen(false)} data-testid="m-nav-referral" className="flex items-center gap-2 px-3 py-3 text-white/80 hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                    <Gift className="w-4 h-4" /> Referral
                  </Link>
                  <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="m-nav-account" className="flex items-center gap-2 px-3 py-3 text-white/80 hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                    <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
                  </Link>
                  <button onClick={() => { setMobileOpen(false); logout(); }} data-testid="m-nav-logout" className="flex items-center gap-2 px-3 py-3 text-left text-white/60 hover:text-[#FF003C] hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} data-testid="m-nav-login" className="flex items-center gap-2 px-3 py-3 text-white/80 hover:bg-white/5 uppercase font-bold tracking-widest text-xs">
                    <User className="w-4 h-4" /> Login
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} data-testid="m-nav-register" className="flex items-center justify-center gap-2 px-3 py-3 bg-[#00F0FF] text-black uppercase font-bold tracking-widest text-xs">
                    Register
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
